import {
  Badge as MantineBadge,
  BadgeProps as MantineBadgeProps,
} from '@mantine/core';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import { useTextOverflow } from '@zorro/web/ui-utils/hooks/useTextOverflow';
import { forwardRef } from 'react';

import styles from './Pill.module.css';

export type PillVariant = 'with-icon' | 'tag' | 'ai-suggestion';

export type PillColor =
  | 'gray'
  | 'green'
  | 'red'
  | 'orange'
  | 'yellow'
  | 'blue'
  | 'indigo'
  | 'steel'
  | 'pink';

export type PillSize = 'sm' | 'md';

export interface PillProps extends Omit<
  MantineBadgeProps,
  | 'variant'
  | 'color'
  | 'styles'
  | 'classNames'
  | 'style'
  | 'size'
  | 'radius'
  | 'gradient'
  | 'autoContrast'
> {
  /**
   * Layout variant: 'with-icon' renders an icon circle + text, 'tag' renders a
   * colored pill, 'ai-suggestion' renders a transparent pill with a gradient
   * ring. 'ai-suggestion' ignores `color`.
   */
  variant?: PillVariant;
  /** Color theme of the pill. Default: 'gray' */
  color?: PillColor;
  /** Show a tooltip with the full content when the label is truncated. */
  withOverflowTooltip?: boolean;
  /**
   * 'sm' is a 16px counter pill (e.g. a count inside a button). Only the 'tag'
   * variant honors it. Default: 'md'
   */
  size?: PillSize;
}

type PillBadgeProps = Omit<PillProps, 'withOverflowTooltip'>;

const PillBadge = forwardRef<HTMLDivElement, PillBadgeProps>(
  ({ variant = 'with-icon', color = 'gray', size = 'md', ...props }, ref) => (
    <MantineBadge
      ref={ref}
      unstyled
      classNames={{
        root: styles.root,
        label: styles.label,
        section: styles.section,
      }}
      data-variant={variant}
      data-color={color}
      data-size={size}
      {...props}
    />
  )
);

const OverflowTooltipPill = forwardRef<HTMLDivElement, PillBadgeProps>(
  ({ children, ...props }, ref) => {
    const { isTextOverflowing, textRef } = useTextOverflow<HTMLSpanElement>(
      typeof children === 'string' ? children : undefined
    );

    return (
      <Tooltip label={children} position="top" disabled={!isTextOverflowing}>
        <PillBadge ref={ref} {...props}>
          <span ref={textRef} className={styles.overflowLabel}>
            {children}
          </span>
        </PillBadge>
      </Tooltip>
    );
  }
);

export const Pill = forwardRef<HTMLDivElement, PillProps>(
  ({ withOverflowTooltip = false, ...props }, ref) =>
    withOverflowTooltip ? (
      <OverflowTooltipPill ref={ref} {...props} />
    ) : (
      <PillBadge ref={ref} {...props} />
    )
);

Pill.displayName = 'Pill';
